import { type CreateReminderRequest, type PropertyDto, remindersApi } from '../../services/api';
import { ApiError } from '../../services/core.api';
import { icon } from '../../icons';
import { t } from '../../services/i18n';
import { esc, formatDate, formatRecurrence } from './reminder-shared';

function getNextOccurrenceDate(
  dueDate: string,
  recurrenceValue?: number,
  recurrenceUnit?: CreateReminderRequest['recurrenceUnit'],
): string {
  const [year, month, day] = dueDate.split('-').map((part) => parseInt(part, 10));
  const nextDate = new Date(Date.UTC(year, month - 1, day));

  if (!recurrenceValue || !recurrenceUnit) {
    return dueDate;
  }

  switch (recurrenceUnit) {
    case 'day':
      nextDate.setUTCDate(nextDate.getUTCDate() + recurrenceValue);
      break;
    case 'month':
      nextDate.setUTCMonth(nextDate.getUTCMonth() + recurrenceValue);
      break;
    case 'year':
      nextDate.setUTCFullYear(nextDate.getUTCFullYear() + recurrenceValue);
      break;
  }

  return nextDate.toISOString().slice(0, 10);
}

export function openReminderCreateDialog(properties: PropertyDto[], onCreated: () => Promise<void>): void {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal">
      <div class="modal-header">
        <h2>${t('reminder.new')}</h2>
        <button class="modal-close" data-action="cancel">${icon('x', { size: 20 })}</button>
      </div>
      <form>
        <div class="form-error" data-form-error hidden></div>
        <div class="form-group">
          <label>${t('common.title')} *</label>
          <input name="title" required placeholder="${t('reminder.titlePlaceholder')}">
        </div>
        <div class="form-group">
          <label>${t('common.deadline')} *</label>
          <input name="dueDate" type="date" required>
        </div>
        <div class="form-group">
          <label>${t('reminder.linkProperty')}</label>
          <select name="propertyId">
            <option value="">${t('common.noLink')}</option>
            ${properties.map((property) => `<option value="${property.id}">${esc(property.displayName || property.address)}</option>`).join('')}
          </select>
        </div>
        <div class="form-group">
          <label>${t('reminder.recurrence')}</label>
          <div style="display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:0.75rem;">
            <input
              name="recurrenceIntervalValue"
              type="number"
              min="1"
              step="1"
              inputmode="numeric"
              placeholder="${t('reminder.recurrenceValuePlaceholder')}"
              aria-label="${t('reminder.recurrenceValueLabel')}">
            <select name="recurrenceUnit" aria-label="${t('reminder.recurrenceUnit')}">
              <option value="day" data-singular-label="${t('reminder.recurrenceUnitDaySingle')}" data-plural-label="${t('reminder.recurrenceUnitDay')}">${t('reminder.recurrenceUnitDay')}</option>
              <option value="month" data-singular-label="${t('reminder.recurrenceUnitMonthSingle')}" data-plural-label="${t('reminder.recurrenceUnitMonth')}">${t('reminder.recurrenceUnitMonth')}</option>
              <option value="year" data-singular-label="${t('reminder.recurrenceUnitYearSingle')}" data-plural-label="${t('reminder.recurrenceUnitYear')}">${t('reminder.recurrenceUnitYear')}</option>
            </select>
          </div>
          <div class="text-sm" data-recurrence-summary>${t('reminder.recurrenceSummaryOneTime')}</div>
          <div class="text-sm text-muted" data-recurrence-date-summary>${t('reminder.recurrenceDateSummaryMissingDeadline')}</div>
          <div class="text-sm text-muted">${t('reminder.recurrenceHint')}</div>
        </div>
        <div class="modal-actions">
          <button type="button" class="btn btn-outline" data-action="cancel">${t('common.cancel')}</button>
          <button type="submit" class="btn btn-primary">${t('common.add')}</button>
        </div>
      </form>
    </div>
  `;
  const close = () => overlay.remove();
  const dueDateInput = overlay.querySelector<HTMLInputElement>('input[name="dueDate"]');
  const recurrenceValueInput = overlay.querySelector<HTMLInputElement>('input[name="recurrenceIntervalValue"]');
  const recurrenceUnitSelect = overlay.querySelector<HTMLSelectElement>('select[name="recurrenceUnit"]');
  const recurrenceSummary = overlay.querySelector<HTMLElement>('[data-recurrence-summary]');
  const recurrenceDateSummary = overlay.querySelector<HTMLElement>('[data-recurrence-date-summary]');
  const updateRecurrenceUi = () => {
    const recurrenceValue = recurrenceValueInput?.value.trim() ? parseInt(recurrenceValueInput.value, 10) : undefined;
    const dueDate = dueDateInput?.value ?? '';

    recurrenceUnitSelect?.querySelectorAll<HTMLOptionElement>('option').forEach((option) => {
      option.textContent = recurrenceValue === 1
        ? option.dataset.singularLabel ?? option.textContent
        : option.dataset.pluralLabel ?? option.textContent;
    });

    if (!recurrenceSummary) return;

    const recurrenceLabel = recurrenceValue && recurrenceUnitSelect?.value
      ? formatRecurrence(recurrenceValue, recurrenceUnitSelect.value as CreateReminderRequest['recurrenceUnit'])
      : '';

    recurrenceSummary.textContent = recurrenceLabel
      ? t('reminder.recurrenceSummaryValue', { recurrence: recurrenceLabel })
      : t('reminder.recurrenceSummaryOneTime');

    if (!recurrenceDateSummary) return;

    if (!dueDate) {
      recurrenceDateSummary.textContent = t('reminder.recurrenceDateSummaryMissingDeadline');
      return;
    }

    const nextOccurrenceDate = getNextOccurrenceDate(
      dueDate,
      recurrenceValue,
      recurrenceUnitSelect?.value as CreateReminderRequest['recurrenceUnit'] | undefined,
    );

    recurrenceDateSummary.textContent = recurrenceLabel
      ? t('reminder.recurrenceDateSummaryRecurring', { date: formatDate(nextOccurrenceDate) })
      : t('reminder.recurrenceDateSummaryOneTime', { date: formatDate(nextOccurrenceDate) });
  };

  overlay.addEventListener('click', (event) => { if (event.target === overlay) close(); });
  overlay.querySelectorAll('[data-action="cancel"]').forEach((button) => button.addEventListener('click', close));
  dueDateInput?.addEventListener('input', updateRecurrenceUi);
  recurrenceValueInput?.addEventListener('input', updateRecurrenceUi);
  recurrenceUnitSelect?.addEventListener('change', updateRecurrenceUi);
  updateRecurrenceUi();
  overlay.querySelector('form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const submitBtn = overlay.querySelector('[type="submit"]') as HTMLButtonElement;
    const errorBox = overlay.querySelector<HTMLElement>('[data-form-error]');
    if (errorBox) {
      errorBox.hidden = true;
      errorBox.textContent = '';
    }
    submitBtn.disabled = true;
    const fd = new FormData(event.currentTarget as HTMLFormElement);
    const recurrenceValueRaw = (fd.get('recurrenceIntervalValue') as string).trim();
    const data: CreateReminderRequest = {
      title: fd.get('title') as string,
      dueDate: fd.get('dueDate') as string,
      propertyId: (fd.get('propertyId') as string) || undefined,
      recurrenceIntervalValue: recurrenceValueRaw ? parseInt(recurrenceValueRaw, 10) : undefined,
      recurrenceUnit: recurrenceValueRaw ? (fd.get('recurrenceUnit') as CreateReminderRequest['recurrenceUnit']) : undefined,
    };
    try {
      await remindersApi.create(data);
      close();
      await onCreated();
    } catch (error) {
      console.error('Failed to create reminder:', error);
      if (errorBox) {
        errorBox.hidden = false;
        errorBox.textContent = error instanceof ApiError ? error.message : t('common.errorGeneric');
      }
      submitBtn.disabled = false;
    }
  });
  document.body.appendChild(overlay);
}
